// Redux slice for the Method library (agency_methods).
//
// A SKILL has METHODS. The agent's TASK is to use a method.
// This slice powers MethodsHome (the library list) and MethodEditor (CRUD).

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

export const fetchAgencyMethods = createAsyncThunk(
  'agencyMethods/fetchList',
  async ({ discipline, status } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (discipline) params.set('discipline', discipline);
      if (status) params.set('status', status);
      const qs = params.toString();
      const res = await authFetch(`/.netlify/functions/agency-methods-list${qs ? `?${qs}` : ''}`, { method: 'GET' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) { return rejectWithValue(err.message); }
  }
);

export const fetchAgencyMethodDetail = createAsyncThunk(
  'agencyMethods/fetchDetail',
  async (key, { rejectWithValue }) => {
    try {
      const res = await authFetch(`/.netlify/functions/agency-methods-get?key=${encodeURIComponent(key)}`, { method: 'GET' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) { return rejectWithValue(err.message); }
  }
);

export const saveAgencyMethod = createAsyncThunk(
  'agencyMethods/save',
  async (method, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/agency-methods-save', {
        method: 'POST',
        body: JSON.stringify(method),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) { return rejectWithValue(err.message); }
  }
);

export const publishAgencyMethod = createAsyncThunk(
  'agencyMethods/publish',
  async (key, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/agency-methods-publish', {
        method: 'POST',
        body: JSON.stringify({ key }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) { return rejectWithValue(err.message); }
  }
);

export const archiveAgencyMethod = createAsyncThunk(
  'agencyMethods/archive',
  async (key, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/agency-methods-archive', {
        method: 'POST',
        body: JSON.stringify({ key }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) { return rejectWithValue(err.message); }
  }
);

const slice = createSlice({
  name: 'agencyMethods',
  initialState: {
    methods: [],
    activeMethod: null,
    usedBy: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearActiveMethod: (state) => { state.activeMethod = null; state.usedBy = []; },
    clearAgencyMethodsError: (state) => { state.error = null; },
  },
  extraReducers: (b) => {
    b.addCase(fetchAgencyMethods.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(fetchAgencyMethods.fulfilled, (s, a) => { s.loading = false; s.methods = a.payload.methods || []; });
    b.addCase(fetchAgencyMethods.rejected, (s, a) => { s.loading = false; s.error = a.payload || 'Failed'; });
    b.addCase(fetchAgencyMethodDetail.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(fetchAgencyMethodDetail.fulfilled, (s, a) => {
      s.loading = false;
      s.activeMethod = a.payload.method || null;
      s.usedBy = a.payload.used_by || [];
    });
    b.addCase(fetchAgencyMethodDetail.rejected, (s, a) => { s.loading = false; s.error = a.payload || 'Failed'; });
    b.addCase(saveAgencyMethod.pending, (s) => { s.saving = true; s.error = null; });
    b.addCase(saveAgencyMethod.fulfilled, (s, a) => { s.saving = false; s.activeMethod = a.payload.method || s.activeMethod; });
    b.addCase(saveAgencyMethod.rejected, (s, a) => { s.saving = false; s.error = a.payload || 'Failed'; });
    b.addCase(publishAgencyMethod.fulfilled, (s, a) => { s.activeMethod = a.payload.method || s.activeMethod; });
    b.addCase(archiveAgencyMethod.fulfilled, (s, a) => {
      const archived = a.payload.method;
      if (archived) s.methods = s.methods.filter((m) => m.key !== archived.key);
    });
  },
});

export const { clearActiveMethod, clearAgencyMethodsError } = slice.actions;
export const selectAgencyMethods = (s) => s.agencyMethods?.methods || [];
export const selectActiveMethod = (s) => s.agencyMethods?.activeMethod || null;
export const selectMethodUsedBy = (s) => s.agencyMethods?.usedBy || [];
export const selectAgencyMethodsLoading = (s) => s.agencyMethods?.loading || false;
export const selectAgencyMethodsSaving = (s) => s.agencyMethods?.saving || false;
export const selectAgencyMethodsError = (s) => s.agencyMethods?.error || null;
export default slice.reducer;
